import "./DeletePagesModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { getDocumentGroupForPage } from "../thumbnails/utils/thumbnailUtils";

import type { DocumentGroup } from "services/document-group-api";
import type { GroupingSource } from "services/metadoc-api";

type DeletePagesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedPages: Set<number>;
  totalPages: number;
  documentGroups?: DocumentGroup[];
  groupingSource?: GroupingSource;
  isDeleting?: boolean;
};

const DeletePagesModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedPages,
  totalPages,
  documentGroups = [],
  groupingSource = "OPS",
  isDeleting = false,
}: DeletePagesModalProps) => {
  const sortedPages = Array.from(selectedPages).sort((a, b) => a - b);
  const pageCount = selectedPages.size;
  const [error, setError] = useState<string>("");

  // ops mode keeps strict front-end validation; analyst mode only blocks full-group deletion
  useEffect(() => {
    if (!isOpen || selectedPages.size === 0) {
      setError("");
      return;
    }

    if (groupingSource === "AI") {
      const isEntireGroupSelected = documentGroups.some((group) => {
        for (let page = group.start_pg; page <= group.end_pg; page += 1) {
          if (!selectedPages.has(page)) {
            return false;
          }
        }

        return true;
      });

      setError(
        isEntireGroupSelected
          ? "To delete entire group pages, delete from the table of contents."
          : "",
      );
      return;
    }

    // check if trying to delete all pages
    if (selectedPages.size === totalPages) {
      setError(
        "Cannot delete all pages from the document. At least one page must remain.",
      );
      return;
    }

    // check if any pages are assigned to document groups
    if (documentGroups.length === 0) {
      setError("");
      return;
    }

    // find pages that are assigned to document groups
    const pagesInGroups: number[] = [];
    const affectedGroups: DocumentGroup[] = [];

    for (const pageNum of selectedPages) {
      const group = getDocumentGroupForPage(pageNum, documentGroups);
      if (group) {
        pagesInGroups.push(pageNum);
        if (!affectedGroups.find((g) => g.id === group.id)) {
          affectedGroups.push(group);
        }
      }
    }

    if (pagesInGroups.length > 0) {
      const pageText =
        pagesInGroups.length === 1
          ? `Page ${pagesInGroups[0]}`
          : `Pages ${pagesInGroups.sort((a, b) => a - b).join(", ")}`;
      setError(
        `${pageText} ${pagesInGroups.length === 1 ? "is" : "are"} assigned to a document group and cannot be deleted.`,
      );
    } else {
      setError("");
    }
  }, [isOpen, selectedPages, totalPages, documentGroups, groupingSource]);

  // keyboard shortcut: Enter to confirm deletion
  useHotkeys(
    "enter",
    (e) => {
      if (isOpen && !isDeleting && !error) {
        e.preventDefault();
        onConfirm();
      }
    },
    {
      enableOnFormTags: false, // don't trigger when typing in inputs
      preventDefault: true,
    },
    [isOpen, isDeleting, error, onConfirm],
  );

  // keyboard shortcut: Esc to cancel
  useHotkeys(
    "escape",
    (e) => {
      if (isOpen && !isDeleting) {
        e.preventDefault();
        onClose();
      }
    },
    {
      enableOnFormTags: false,
      preventDefault: true,
    },
    [isOpen, isDeleting, onClose],
  );

  // format page ranges for display
  const formatPageRanges = () => {
    if (pageCount === 0) return "";
    if (pageCount === 1) return `Page ${sortedPages[0]}`;

    // group consecutive pages into ranges
    const ranges: string[] = [];
    let rangeStart = sortedPages[0];
    let rangeEnd = sortedPages[0];

    for (let i = 1; i < sortedPages.length; i++) {
      if (sortedPages[i] === rangeEnd + 1) {
        rangeEnd = sortedPages[i];
      } else {
        // add the completed range
        ranges.push(
          rangeStart === rangeEnd
            ? `${rangeStart}`
            : `${rangeStart}-${rangeEnd}`,
        );
        rangeStart = sortedPages[i];
        rangeEnd = sortedPages[i];
      }
    }

    // add the final range
    ranges.push(
      rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`,
    );

    return `Page${pageCount > 1 ? "s" : ""} ${ranges.join(", ")}`;
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c delete-pages-modal__footer">
          <Button
            className="delete delete-pages-modal__footer-button"
            onClick={onConfirm}
            disabled={isDeleting || !!error}
          >
            {isDeleting ? "Deleting..." : "Confirm Delete"}
            <span className="delete-pages-modal__button-shortcut">ENTER</span>
          </Button>
          <Button
            className="delete-pages-modal__footer-button"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
            <span className="delete-pages-modal__button-shortcut">ESC</span>
          </Button>
        </div>
      }
    >
      <Card heading="Delete Pages from MetaDoc">
        <p>
          Are you sure you want to delete{" "}
          <strong>
            {pageCount} page{pageCount !== 1 ? "s" : ""}
          </strong>{" "}
          from this metadoc?
        </p>
        <div className="note-preview">
          <strong>{formatPageRanges()}</strong>
        </div>
        {error && <div className="error-message">{error}</div>}
        <p>
          This will permanently remove these pages from the PDF. This action
          cannot be undone.
        </p>
      </Card>
    </KaboodleModal>
  );
};

export default DeletePagesModal;
